import { Facilities, Facility, FacilityGroups, useAuth } from "@app/shared";
import { FC, MouseEventHandler, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Pages } from ".";
import { toastSuccess } from "../../helpers";
import { useConfirmDialog, useFetch } from "../../hooks";
import { useEditModelDialog } from "../../hooks/useEditModelDialog";
import { DashboardLayout } from "../layouts";
import { EditFacilityDetailsInputs, IconLink, Icons, SortableTable } from "../shared";
import { DropDownMenu } from "../shared/DropDownMenu";
import { NavSelectorDropdown } from "../shared/NavSelectorDropdown";

export const ViewFacilityGroup: FC = (props) => {
    const { id } = useParams();
    const { currentOrganizationId } = useAuth();
    const [facilities, , fetchHelpers] = useFetch(() => Facilities.getByGroupId(id), [id]);
    const [allGroups] = useFetch(() => FacilityGroups.getAllFacilityGroupOptions());
    const facilityDialog = useEditModelDialog(Facility);
    const confirm = useConfirmDialog();
    const navigate = useNavigate();

    const groupName = useMemo(
        () => allGroups?.find((g) => g.value === id)?.label ?? "",
        [allGroups, id]
    );

    const handleAddOnClick: MouseEventHandler = () => {
        facilityDialog.show(async (newFacility) => {
            var newId = await Facilities.save(newFacility as Facility);
            if (newId) {
                navigate(Pages.FacilityDetails.getUrl({ id: newId }));
            }

        }, "Add New Location", { facilityGroupId: id } as Partial<Facility>);
    };

    const handleDeleteOnClick = (f: Facility) => {
        const sensors = (f.devices ?? []).map((d) => d.name || d.deviceKey).filter(Boolean);
        const one = sensors.length === 1;
        const noun = one ? "sensor" : "sensors";
        const text = sensors.length
            ? `${f.name} has ${sensors.length} ${noun} assigned: ${sensors.join(", ")}. Deleting it disconnects ${one ? "it" : "them"} and clears ${one ? "its" : "their"} zones and alerts. ${one ? "It" : "They"} will show as unassigned and can be set up again from Devices.`
            : `Are you sure you want to delete ${f.name}?`;
        const okText = sensors.length ? `Delete and disconnect ${sensors.length} ${noun}` : "Delete";

        confirm.show("Delete Location?", text, okText, async () => {
            await Facilities.deleteFacility(f.id);
            toastSuccess("Deleted", sensors.length
                ? `Location deleted, ${sensors.length} ${noun} disconnected`
                : "Location has been deleted");
            fetchHelpers.refreshData();
        });
    };

    return (
        <DashboardLayout
            centerChildren="Locations"
            leftChildren={
                <NavSelectorDropdown
                    mode="orgs-groups"
                    triggerLabel={groupName || "Select Group"}
                    initialOrgId={currentOrganizationId}
                    initialGroupId={id}
                />
            }>
            <>
                <div className="flex justify-between items-center mb-4">
                    <div></div>
                    <IconLink
                        Icon={Icons.MapMarker}
                        iconSize={20}
                        onClick={() => navigate(Pages.FacilitiesMapView.getUrl({ id: id! }))}
                    >
                        View Locations On Map
                    </IconLink>
                </div>
                <SortableTable
                    isLoading={fetchHelpers.isLoading}
                    emptyValue={
                        <div className="py-5">
                            <p className="py-3">
                                No locations were found.
                            </p>
                            <IconLink Icon={Icons.PlusCircle} iconSize={30} onClick={handleAddOnClick}>Add New Location</IconLink>
                        </div>
                    }
                    columns={[
                        {
                            header: "Name",
                            dataKey: "name",
                            width: 250,
                            filterable: true,
                            filterType: 'text'
                        },
                        {
                            header: "Sensors",
                            dataKey: "deviceCount",
                            width: 150,
                            filterable: true,
                            filterType: 'number'
                        },
                        {
                            header: <IconLink iconSize={20} Icon={Icons.PlusCircle} onClick={handleAddOnClick} />,
                            width: 10,
                            renderFunc: f => (<DropDownMenu>
                                <IconLink Icon={Icons.Trash} onClick={() => handleDeleteOnClick(f!)}>Delete</IconLink>
                            </DropDownMenu>)
                        }
                    ]}
                    onRowClick={f => {
                        navigate(Pages.FacilityDetails.getUrl({ id: f.id! }))
                    }}
                    data={facilities}
                    pagination
                    defaultPageSize={10}
                    pageSizeOptions={[5, 10, 25, 50]}
                    filtering
                />
                {facilityDialog.renderDialog((model, helpers) => (
                    <EditFacilityDetailsInputs model={model} helpers={helpers} />
                ))}
                {confirm.renderDialog()}
            </>
        </DashboardLayout>
    );
}
